import { useEffect, useState } from 'react';
import { KhaiBaoVatTuThayThe, KhaiBaoVatTuThayTheInput } from '@/types/schemas';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseKhaiBaoVatTuThayTheReturn {
  khaiBaoVatTuThayThes: KhaiBaoVatTuThayThe[];
  isLoading: boolean;
  addKhaiBaoVatTuThayThe: (newKhaiBaoVatTuThayThe: KhaiBaoVatTuThayTheInput) => Promise<void>;
  updateKhaiBaoVatTuThayThe: (uuid: string, updatedKhaiBaoVatTuThayThe: KhaiBaoVatTuThayTheInput) => Promise<void>;
  deleteKhaiBaoVatTuThayThe: (uuid: string) => Promise<void>;
  refreshKhaiBaoVatTuThayThes: () => Promise<void>;
  getKhaiBaoVatTuThayThesByPeriod: (ky: number, nam: number, unitId?: string) => Promise<KhaiBaoVatTuThayThe[]>;
  copyFromPreviousPeriod: (ky: number, nam: number, unitId?: string) => Promise<void>;
}

export const useKhaiBaoVatTuThayThe = (
  initialKhaiBaoVatTuThayThes: KhaiBaoVatTuThayThe[] = []
): UseKhaiBaoVatTuThayTheReturn => {
  const [khaiBaoVatTuThayThes, setKhaiBaoVatTuThayThes] = useState<KhaiBaoVatTuThayThe[]>(initialKhaiBaoVatTuThayThes);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchKhaiBaoVatTuThayThes = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get(`/entities/${entity.slug}/erp/${QUERY_KEYS.KHAI_BAO_VAT_TU_THAY_THE}/`);
      const mappedData: KhaiBaoVatTuThayThe[] = response.data.results;
      setKhaiBaoVatTuThayThes(mappedData);
    } catch (error) {
      console.error('Error fetching material substitution declarations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getKhaiBaoVatTuThayThesByPeriod = async (
    ky: number,
    nam: number,
    unitId?: string
  ): Promise<KhaiBaoVatTuThayThe[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const params: Record<string, any> = { ky, nam };
      if (unitId) {
        params.unit_id = unitId;
      }

      const response = await api.get(`/entities/${entity.slug}/erp/${QUERY_KEYS.KHAI_BAO_VAT_TU_THAY_THE}/`, {
        params
      });
      const mappedData: KhaiBaoVatTuThayThe[] = response.data.results;
      return mappedData;
    } catch (error) {
      console.error('Error fetching material substitution declarations by period:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const copyFromPreviousPeriod = async (ky: number, nam: number, unitId?: string): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      // Calculate previous period
      let prevKy = ky - 1;
      let prevNam = nam;
      if (prevKy < 1) {
        prevKy = 12;
        prevNam = nam - 1;
      }

      // Get declarations from previous period
      const prevDeclarations = await getKhaiBaoVatTuThayThesByPeriod(prevKy, prevNam, unitId);

      // Create new declarations for current period
      for (const prevDeclaration of prevDeclarations) {
        await addKhaiBaoVatTuThayThe({
          unit_id: prevDeclaration.unit_id,
          ky,
          nam,
          ma_vt: prevDeclaration.ma_vt || '',
          ma_vt0: prevDeclaration.ma_vt0 || ''
        });
      }
    } catch (error) {
      console.error('Error copying from previous period:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const addKhaiBaoVatTuThayThe = async (newKhaiBaoVatTuThayThe: KhaiBaoVatTuThayTheInput) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.post(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.KHAI_BAO_VAT_TU_THAY_THE}/`,
        newKhaiBaoVatTuThayThe
      );
      const addedKhaiBaoVatTuThayThe: KhaiBaoVatTuThayThe = response.data;
      setKhaiBaoVatTuThayThes(prev => [...prev, addedKhaiBaoVatTuThayThe]);
    } catch (error) {
      console.error('Error adding material substitution declaration:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateKhaiBaoVatTuThayThe = async (uuid: string, updatedKhaiBaoVatTuThayThe: KhaiBaoVatTuThayTheInput) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.put(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.KHAI_BAO_VAT_TU_THAY_THE}/${uuid}/`,
        updatedKhaiBaoVatTuThayThe
      );
      const updatedData: KhaiBaoVatTuThayThe = response.data;
      setKhaiBaoVatTuThayThes(prev => prev.map(item => (item.uuid === updatedData.uuid ? updatedData : item)));
    } catch (error) {
      console.error('Error updating material substitution declaration:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteKhaiBaoVatTuThayThe = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.KHAI_BAO_VAT_TU_THAY_THE}/${uuid}/`);
      setKhaiBaoVatTuThayThes(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting material substitution declaration:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchKhaiBaoVatTuThayThes();
  }, [entity?.slug]);

  return {
    khaiBaoVatTuThayThes,
    isLoading,
    addKhaiBaoVatTuThayThe,
    updateKhaiBaoVatTuThayThe,
    deleteKhaiBaoVatTuThayThe,
    refreshKhaiBaoVatTuThayThes: fetchKhaiBaoVatTuThayThes,
    getKhaiBaoVatTuThayThesByPeriod,
    copyFromPreviousPeriod
  };
};
